"use client";
// @ts-strict-ignore
import * as React from "react";

import { useWidthObserver } from "hooks/useWidthObserver";
import dayjs from "dayjs";
import type {
  CryptopediaAuthorType,
  CryptopediaCategoryType,
  CryptopediaArticleType
} from "types/cryptopedia";
import FloatingTableOfContents from "components/pages/Cryptopedia/FloatingTableOfContents";
import {
  Heading2,
  Heading4,
  Heading6,
  Paragraph1,
  Paragraph3,
  Heading5
} from "components/parts/Typography/cryptopedia";
import ParseRichText from "components/parts/ParseRichText";
import EntryLink from "components/parts/NextLink/EntryLink";
import CryptopediaAuthor from "components/parts/CryptopediaAuthor";
import NextImage from "components/parts/NextImage";
import CryptopediaTopicTag from "components/parts/CryptopediaTopicTag";
import { getReadTimeForRichText } from "utils/pageReadTime";
import { getCategoryUrl } from "utils/cryptopedia";
import ArticleFeedback from "components/pages/Cryptopedia/Article/ArticleFeedback";
import Bread from "components/pages/Cryptopedia/Article/Breadcrumbs";
import RelatedArticles from "components/pages/Cryptopedia/Article/RelatedArticles";
import ShareIcon from "public/images/cryptopedia/share.svg";
import twitterIcon from "public/images/cryptopedia/cryptopedia_social_twitter.svg";
import linkedInIcon from "public/images/cryptopedia/cryptopedia_social_linkedin.svg";
import facebookIcon from "public/images/cryptopedia/cryptopedia_social_facebook.svg";

import TextLink from "components/parts/NextLink/TextLink";
import EmbeddedAsset from "components/pages/Cryptopedia/Article/EmbeddedAsset";
import type { TableOfContents as TableOfContentsType } from "utils/tableOfContents";
import kebabCase from "lodash/kebabCase";
import { logger } from "observability";
import Image from "next/image";
import { handleContentfulLoader } from "utils/images/contentfulLoader";
import { usePathname } from "i18n/routing";
import { useMixpanelTrackScrolling } from "utils/hooks";

interface CryptopediaSlugPageProps {
  currentArticle: CryptopediaArticleType;
  relatedArticles: CryptopediaArticleType[];
  tableOfContents: TableOfContentsType[];
  mainCategory: CryptopediaCategoryType;
}

export const getAuthorsDisplayTitle = (authors: CryptopediaAuthorType[]) => {
  const jobTitle = authors
    .filter((author) => author.jobTitle)
    .map((author) => {
      return (
        <span key={author.id} className="pl-0">
          {author.jobTitle}
        </span>
      );
    });
  return jobTitle.length > 0 ? jobTitle : null;
};

export function getAuthorsDisplayName(authors: CryptopediaAuthorType[]) {
  return authors.map((author, index) => {
    return (
      <TextLink
        href={`/cryptopedia/authors#${kebabCase(author.name)}`}
        key={author.id}
        className="text-blue-500 hover:underline"
      >
        {index !== 0 ? " & " : null}
        {author.name}
      </TextLink>
    );
  });
}

export const getArticleDateDisplay = (createdAt, updatedAt) => {
  const format = "MMMM D, YYYY";
  if (updatedAt) {
    return `Updated ${dayjs(updatedAt).format(format)}`;
  }

  return `${dayjs(createdAt).format(format)}`;
};

const BodyRichTextConfig = {
  Header2: (props) => (
    <Heading4 as={"h2"} bold className="mt-12 mb-0" {...props} />
  ),
  Header3: (props) => (
    <Heading5 as={"h3"} bold className="mt-12 mb-0" {...props} />
  ),
  Paragraph: Paragraph1,
  EntryLink: (props) => (
    <EntryLink className="text-blue-500 hover:underline" {...props} />
  ),
  Link: (props) => (
    <TextLink className="text-blue-500 hover:underline" {...props} />
  ),
  EmbeddedAsset: EmbeddedAsset,
  ListItem: (props) => (
    <Paragraph1 as={"li"} className="ml-5 list-disc" {...props} />
  )
};

const SummaryRichTextConfig = {
  Link: (props) => (
    <TextLink className="text-blue-500 hover:underline" {...props} />
  ),
  Paragraph: (props) => <Paragraph1 className="italic" {...props} />
};

export const ArticleSocialShare = ({ url, title }) => {
  const { isDesktop } = useWidthObserver();

  const supportsNativeShare =
    typeof navigator !== "undefined" && !!navigator.share;
  const onMobileShare = async () => {
    if (supportsNativeShare) {
      try {
        await navigator.share({
          title: title || url,
          url
        });
      } catch (e) {
        logger.warn("Unable to share article", e);
      }
    }
  };

  if (!isDesktop && supportsNativeShare) {
    return (
      <button
        role="button"
        className="flex h-8 w-8 items-center justify-center"
        onClick={onMobileShare}
      >
        <NextImage src={ShareIcon.src} alt="share" width={25} height={25} />
      </button>
    );
  } else {
    return (
      <div className="flex space-x-4">
        <a
          href={`https://twitter.com/share?url=${url}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <NextImage
            src={twitterIcon.src}
            alt="share with twitter"
            width={25}
            height={25}
          />
        </a>
        <a
          href={`https://www.linkedin.com/shareArticle?url=${url}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <NextImage
            src={linkedInIcon.src}
            alt="share with linkedin"
            width={25}
            height={25}
          />
        </a>
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <NextImage
            src={facebookIcon.src}
            alt="share with facebook"
            width={25}
            height={25}
          />
        </a>
      </div>
    );
  }
};

const CryptopediaArticleSlugView = ({
  currentArticle,
  relatedArticles,
  tableOfContents,
  mainCategory
}: CryptopediaSlugPageProps) => {
  useMixpanelTrackScrolling({
    mixpanelEventName: "Scrolled on Cryptopedia Articles",
    properties: {
      "article name": currentArticle?.title ? currentArticle.title : ""
    }
  });
  const pathname = usePathname();
  const {
    title,
    subtitle,
    authors = [],
    createdAt,
    updatedAt,
    featuredImage,
    summary,
    body,
    topics = [],
    id
  } = currentArticle;
  const url = `https://www.gemini.com${pathname}`;
  const authorTitle = getAuthorsDisplayTitle(authors);

  return (
    <div className="flex">
      <FloatingTableOfContents
        contents={tableOfContents}
        mainCategory={mainCategory}
        title={title}
        topics={topics}
      />

      <div className="mx-auto flex w-full max-w-3xl flex-col px-4 py-2">
        <div className="text-sm text-gray-600">
          <Bread>
            <Bread.Crumb href={getCategoryUrl(mainCategory.name)}>
              {mainCategory.name}
            </Bread.Crumb>
            <Bread.Crumb href={`/cryptopedia/topic/${topics[0]?.slug}`}>
              {topics[0]?.name}
            </Bread.Crumb>
          </Bread>
        </div>
        <Heading2 as="h1">{title}</Heading2>
        {!!subtitle && <Paragraph1 className="my-4">{subtitle}</Paragraph1>}
        <div className="flex items-start justify-between">
          <div>
            <Paragraph3 bold className="text-secondary m-0">
              By {getAuthorsDisplayName(authors)}
              {authorTitle && `,`} {authorTitle}
            </Paragraph3>
            <Paragraph3 className="text-secondary mt-0 mb-6">
              {getArticleDateDisplay(createdAt, updatedAt)} &bull;{" "}
              {getReadTimeForRichText(body.content)} read
            </Paragraph3>
          </div>
          <ArticleSocialShare url={url} title={currentArticle.title} />
        </div>

        {!!featuredImage.url && (
          <Image
            alt={(featuredImage.title || featuredImage.fileName) ?? ""}
            priority
            className="h-auto w-full"
            src={featuredImage.url}
            height={720}
            width={1280}
            loader={handleContentfulLoader}
            quality={100}
          />
        )}
        {!!summary && !!summary.content && (
          <div className="border-secondary border-b pb-12">
            <Heading4 bold>Summary</Heading4>
            <ParseRichText
              source={summary.content}
              config={SummaryRichTextConfig}
            />
          </div>
        )}
        {!!body.content && (
          <div className="pb-8">
            <ParseRichText source={body.content} config={BodyRichTextConfig} />
          </div>
        )}
        <div className="py-6 text-xs text-gray-600 italic">
          Cryptopedia does not guarantee the reliability of the Site content and
          shall not be held liable for any errors, omissions, or inaccuracies.
          The opinions and views expressed in any Cryptopedia article are solely
          those of the author(s) and do not reflect the opinions of Gemini or
          its management. The information provided on the Site is for
          informational purposes only, and it does not constitute an endorsement
          of any of the products and services discussed or investment,
          financial, or trading advice. A qualified professional should be
          consulted prior to making financial decisions. Please visit our{" "}
          <TextLink
            href="/legal/cryptopedia-site-policy#section-cryptopedia-site-policy"
            className="text-blue-500 hover:underline"
          >
            Cryptopedia Site Policy
          </TextLink>{" "}
          to learn more.
        </div>
        {authors.map((author) => (
          <CryptopediaAuthor key={author.id} author={author} />
        ))}
        <ArticleFeedback articleId={id} />
        <div>
          <Heading6 bold className="mt-12 mb-3">
            Topics in article
          </Heading6>
          <div className="flex">
            {topics.map(({ slug, id, name }) => (
              <CryptopediaTopicTag
                text={name}
                key={id}
                href={`/cryptopedia/topic/${slug}`}
              />
            ))}
          </div>
        </div>
        <RelatedArticles posts={relatedArticles} />
      </div>
    </div>
  );
};

export default CryptopediaArticleSlugView;
